import { AsyncPipe } from "@angular/common";
import { Component, ElementRef, Inject, Input, OnDestroy, OnInit } from "@angular/core";
import { Adb2cService } from "@app/blocks/adb2c/services/adb2c/adb2c.service";
import { LoggerService } from "@app/blocks/logger/services/logger.service";
import { ConfigurationService } from "@app/core/shell/services/configuration.service";
import { WINDOW, WINDOW_PROVIDERS } from "@app/shared/services/window/window.service";
import { BehaviorSubject, Subject, Subscription, takeUntil } from "rxjs";
import { ApplicationStatusService } from "./application-status.service";

@Component({
   selector: "app-application-status",
   templateUrl: "./application-status.component.html",
   imports: [AsyncPipe],
   providers: [WINDOW_PROVIDERS],
})
export class ApplicationStatusComponent implements OnInit, OnDestroy {
   @Input() statusContent: string | null = null;
   @Input() isTenant!: boolean;
   @Input() paymentsAvailable: boolean = true;
   @Input() paymentsUnavailableText: string = "";
   showApplicationStatusTitle = false;
   showApplicationStatus = new BehaviorSubject(false);
   title = "Your Application";
   statuses: undefined | any[] = undefined;
   selectedStatus: any | null = null;
   application: any;
   loading = false;
   config = this.configService.getConfig();
   isDisabled = false;

   private destroy$ = new Subject<void>();
   private userSubscription?: Subscription;
   constructor(
      private logger: LoggerService,
      private applicationStatusService: ApplicationStatusService,
      private configService: ConfigurationService,
      private adb2cService: Adb2cService,
      @Inject(WINDOW) private window: Window,
      private el: ElementRef
   ) {}

   ngOnInit(): void {
      const cpt = this.el.nativeElement;
      this.statusContent = cpt.getAttribute("statusContent");
      const isTenantAttr = cpt.getAttribute("istenant");
      this.isTenant = isTenantAttr === "true" ? true : false;

      if (this.statusContent) {
         this.statuses = JSON.parse(this.statusContent);
      }

      // HTML attributes are case-insensitive and get lowercased
      const paymentsAvailableAttr = cpt.getAttribute("paymentsavailable");
      if (paymentsAvailableAttr !== null) {
         // Default is true, so only set to false if attribute is 'false' or 'False'
         this.paymentsAvailable = paymentsAvailableAttr !== "false" && paymentsAvailableAttr !== "False";
      }

      const paymentsUnavailableTextAttr = cpt.getAttribute("paymentsunavailabletext");

      this.logger.info("applicationStatusComponent received statusContent", this.statusContent);
      if (this.isTenant !== null) {
         this.adb2cService.getUser();
         this.userSubscription = this.adb2cService.activeUser
            .asObservable()
            .pipe(takeUntil(this.destroy$))
            .subscribe((user) => {
               //oid and sub both contain the guid userid, however oid is more reliable for applicants which have just
               //logged in for the first time as there can be a delay in the sub claim being available in the token.
               // In cases where sub is not present we can fallback to using oid with a warning log to indicate this scenario.
               const userId = user?.sub ?? user?.oid;
               if (!user?.sub) {
                  this.logger.warning("applicationStatusComponent: sub claim not present, falling back to oid");
               }
               if (user && userId) {
                  this.applicationStatusService.getApplicationStatus(this.isTenant, userId)?.then(
                     (response: any) => {
                        if (response) {
                           this.loading = false;
                           this.logger.info("applicationStatusService data", response);

                           const signup = this.config?.shell.switches.enableSignup;
                           if (signup) {
                              this.application = {
                                 applications: this.applicationStatusService.formatApplications(response.applications, signup),
                                 liveApplications: response.liveApplications,
                              };

                              // Initialize enable properties and apply PaymentsAvailable logic
                              this.application.applications.forEach((app: any) => {
                                 // Default: enable is true
                                 app.enableApply = true;
                                 app.enableWithdraw = true;
                                 app.extraMessage = null;

                                 // Only disable buttons for specific statuses when payments are not available
                                 if (!this.paymentsAvailable) {
                                    const statusLower = app.status.toLowerCase();
                                    if (
                                       statusLower === "approved" ||
                                       statusLower === "pending additional details" ||
                                       statusLower === "pending payment"
                                    ) {
                                       app.enableApply = false;
                                       app.enableWithdraw = false;
                                       app.extraMessage = paymentsUnavailableTextAttr;
                                    }
                                 }
                              });
                           }

                           this.logger.info("application status component", this.application);

                           if (this.application.applications.length < 1) {
                              this.showApplicationStatusTitle = true;
                           } else {
                              this.showApplicationStatus.next(true);

                              if (this.application.applications.length > 1) {
                                 this.title = "Your Applications";
                              } else {
                                 this.title = "Your Application";
                              }
                           }

                           if (this.application.applications.length > 0) {
                              const status = this.application.applications[0].status.toLowerCase();
                              if (this.statuses && this.statuses.length > 0) {
                                 this.statuses.forEach((value) => {
                                    if (value.Status.toLowerCase() === status) {
                                       this.selectedStatus = value;
                                    }
                                 });
                              }
                           }
                        }
                        this.loading = false;
                     },
                     (error) => {
                        console.log("failed to get application status", error);
                     }
                  );
               }
            });
      }
   }

   apply(applicationId: string, reference: string, asset: string, buttonText: string, buttonUrl: string) {
      this.logger.info("apply clicked ", applicationId);
      this.logger.info("signup enabled on apply", this.config?.shell.switches.enableSignup); //pull config out and reuse

      // save applicationId to session storage
      this.logger.info("storing application ref details");
      sessionStorage.setItem("connect.applicationId", applicationId.toString());
      sessionStorage.setItem("connect.crmReference", reference);
      sessionStorage.setItem("connect.assetAddress", asset);

      let nextUrl = this.window.location.protocol + "//" + this.window.location.host;

      if (buttonUrl !== "" && buttonUrl !== null) {
         nextUrl = this.window.location.protocol + "//" + this.window.location.host + buttonUrl;
      }

      this.logger.info("nextUrl" + nextUrl);

      this.window.location = nextUrl;
   }

   withdraw(applicationId: string) {
      this.logger.info("withdraw clicked ", applicationId);

      this.isDisabled = true;

      this.applicationStatusService
         .withdrawApplication(applicationId)
         ?.then(() => {
            this.isDisabled = false;
            const nextUrl = this.window.location.protocol + "//" + this.window.location.host + "/";
            this.window.location = nextUrl;
         })
         .catch((error) => {
            this.isDisabled = false;
            this.logger.error("withdraw application error", error);
            return null;
         });
   }

   showWithdrawModal(applicationId: string) {
      $(`#withdraw-modal-${applicationId}`).modal({
         keyboard: false,
         show: true,
      });
   }

   hideWithdrawModal(applicationId: string) {
      $(`#withdraw-modal-${applicationId}`).modal("hide");
   }

   ngOnDestroy() {
      this.destroy$.next();
      this.destroy$.complete();
   }
}
